import React, { useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { GripVerticalIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { DELIVERABLE_STATUSES, DELIVERABLE_TYPES, VIDEO_FORMATS } from '../../types/portal';
import type { Deliverable } from '../../types/portal';
import { isVersioned, linkFieldsFor, metaLabel, statusOptionLabel, waitingLabel } from '../../utils/deliverables';
import { VersionHistory } from './VersionHistory';
import { TranscriptUpload } from './TranscriptUpload';
import { TranscriptRevisions } from './TranscriptRevisions';
import { isDriveFolder, toDrivePreviewUrl } from '../../utils/drive';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
interface DeliverableAdminProps {
  deliverable: Deliverable;
  /** Reports which row is currently lifted, or null once it is set down. */
  onLift: (id: string | null) => void;
}

/** "V3" → "V4". A blank version starts at V1; anything else is left alone. */
function nextVersion(current: string): string {
  const match = /^v(\d+)$/i.exec(current.trim());
  if (match) return `V${Number(match[1]) + 1}`;
  return current.trim() ? current.trim() : 'V1';
}
export function DeliverableAdmin({
  deliverable,
  onLift
}: DeliverableAdminProps) {
  const dragControls = useDragControls();
  const [carrying, setCarrying] = useState(false);
  const {
    updateDeliverable,
    removeDeliverable,
    duplicateDeliverable,
    publishVersion,
    resolveChangeRequest,
    notify,
    focusDeliverableId
  } = usePortal();

  /** 'details' shows the client feedback; 'edit' also shows the configuration. */
  const [panel, setPanel] = useState<'closed' | 'details' | 'edit'>(focusDeliverableId === deliverable.id ? 'details' : 'closed');
  const open = panel !== 'closed';
  const editingRow = panel === 'edit';
  const setOpen = (next: boolean) => setPanel(next ? 'edit' : 'closed');
  const [more, setMore] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [draft, setDraft] = useState<Deliverable>(deliverable);
  const links = linkFieldsFor(draft.type);
  const versioned = isVersioned(draft.status);
  const typed = draft.version.trim();
  /* What publishing will actually create: whatever staff typed, or the next up. */
  const target = typed && typed !== deliverable.version ? typed : nextVersion(deliverable.version);
  function edit(patch: Partial<Deliverable>) {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  }
  function save() {
    updateDeliverable(deliverable.id, {
      name: draft.name.trim() || deliverable.name,
      type: draft.type,
      status: draft.status,
      version: versioned ? draft.version : '',
      driveUrl: draft.driveUrl,
      audioUrl: draft.audioUrl,
      videoFormat: draft.type === 'Video' ? draft.videoFormat ?? '16:9 Landscape' : undefined,
      visibleToClient: draft.visibleToClient,
      transcript: draft.transcript
    });
    notify(`${draft.name || deliverable.name} · changes saved`);
    setOpen(false);
  }
  function publish() {
    updateDeliverable(deliverable.id, {
      driveUrl: draft.driveUrl,
      audioUrl: draft.audioUrl
    });
    publishVersion(deliverable.id, target);
    edit({
      version: target,
      status: 'Ready for Review'
    });
    notify(`${target} published. The client can now review this version.`);
  }
  const outstanding = deliverable.changeRequests.filter(request => !request.resolved);
  /** Completed requests stay on the record, newest first. */
  const completed = deliverable.changeRequests.filter(request => request.resolved).reverse();
  return <Reorder.Item value={deliverable} dragListener={false} dragControls={dragControls} onDragStart={() => {
    setCarrying(true);
    onLift(deliverable.id);
  }} onDragEnd={() => {
    setCarrying(false);
    onLift(null);
  }}
  /* The row itself is carried, so it stays fully visible and readable. */ animate={carrying ? {
    backgroundColor: 'rgba(255,255,255,0.05)',
    boxShadow: '0 18px 40px rgba(0,0,0,0.55)'
  } : {
    backgroundColor: 'rgba(255,255,255,0)',
    boxShadow: '0 0 0 rgba(0,0,0,0)'
  }} transition={{
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }} style={{
    position: 'relative',
    zIndex: carrying ? 30 : 0
  }} className={['border-b border-ruleDark', carrying ? 'cursor-grabbing rounded-[2px] ring-1 ring-paper/25' : ''].join(' ')} data-id="element-174" data-mp="zas7dp30bqm0">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4 px-3 py-6" data-id="element-175" data-mp="17gm7mikase3t">
        <span role="button" tabIndex={0} onPointerDown={event => {
        event.preventDefault();
        dragControls.start(event);
      }} aria-label={`Reorder ${deliverable.name}`} title="Drag to reorder" className="shrink-0 touch-none cursor-grab text-paper/30 transition-colors duration-150 ease-out hover:text-paper/70 active:cursor-grabbing" data-id="element-176" data-mp="too54ql3dggm">
          <GripVerticalIcon className="h-5 w-5" strokeWidth={1.25} data-id="element-177" data-mp="10mvityd1vw9" />
        </span>

        {/* The row itself opens the deliverable's details and client feedback. */}
        <button type="button" onClick={() => setPanel(current => current === 'details' ? 'closed' : 'details')} aria-expanded={open} className="min-w-0 flex-1 text-left" data-id="element-178" data-mp="f1pid8x6ja88">
          <p className="font-display text-2xl leading-none sm:text-3xl" data-id="element-179" data-mp="nyq5zdcfd4am">{deliverable.name}</p>
          <p className="label-sm mt-3 text-paper/40" data-id="element-180" data-mp="jxocld56vmgk">
            {metaLabel(deliverable)} · {deliverable.status}
            {outstanding.length > 0 ? ` · ${outstanding.length} change${outstanding.length === 1 ? '' : 's'} requested` : ''}
          </p>
        </button>

        <button type="button" onClick={() => {
        updateDeliverable(deliverable.id, {
          visibleToClient: !deliverable.visibleToClient
        });
        edit({
          visibleToClient: !deliverable.visibleToClient
        });
      }} aria-pressed={deliverable.visibleToClient} title="Shown in Client Portal" className={['label shrink-0 border px-3 py-2 transition-colors duration-150 ease-out', deliverable.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-181" data-mp="17mpeehjinzcv">
          {deliverable.visibleToClient ? 'Shown' : 'Hidden'}
        </button>

        <div className="relative flex shrink-0 items-center gap-6" data-id="element-182" data-mp="22i43jtdxvabg">
          <button type="button" onClick={() => {
          setDraft(deliverable);
          setPanel(current => current === 'edit' ? 'closed' : 'edit');
          setConfirmRemove(false);
          setMore(false);
        }} className={BUTTON_QUIET} data-id="element-183" data-mp="22frlzhc3f1yx">
            {editingRow ? 'Close' : 'Edit'}
          </button>

          {/* Occasional actions, kept off the everyday row. */}
          <button type="button" onClick={() => setMore(current => !current)} aria-expanded={more} aria-label="More options" className="label text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-184" data-mp="sez0ajxgs8s">
            More
          </button>

          {more && <div className="absolute right-0 top-full z-40 mt-3 w-52 border border-paper/25 bg-ink" data-id="element-185" data-mp="og6nsdmnmxqy">
              <button type="button" onClick={() => {
            duplicateDeliverable(deliverable.id);
            setMore(false);
            notify(`${deliverable.name} duplicated`);
          }} className="label block w-full border-b border-ruleDark px-5 py-4 text-left text-paper/70 transition-colors duration-150 ease-out hover:text-paper" data-id="element-186" data-mp="jh3g0lfaidfa">
                Duplicate
              </button>
              <button type="button" onClick={() => {
            setConfirmRemove(true);
            setMore(false);
          }} className="label block w-full px-5 py-4 text-left text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-187" data-mp="3jxs9jvzpua8c">
                Remove
              </button>
            </div>}
        </div>
      </div>

      {confirmRemove && <div className="mb-6 border border-paper/30 px-6 py-6" data-id="element-188" data-mp="bk33khxsgw0j">
          <p className="font-display text-xl leading-snug" data-id="element-189" data-mp="ble8il2z09t8">
            Remove {deliverable.name} from the dashboard?
          </p>
          <p className="label-sm mt-3 text-paper/40" data-id="element-190" data-mp="1t8gxcgmruek">
            This only removes it from the client dashboard. Nothing is deleted from Google Drive.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-6" data-id="element-191" data-mp="phhyu3aq3y0f">
            <button type="button" onClick={() => {
          removeDeliverable(deliverable.id);
          notify(`${deliverable.name} removed from dashboard`);
        }} className={BUTTON_SOLID} data-id="element-192" data-mp="uaujy3g71mof">
              Remove
            </button>
            <button type="button" onClick={() => setConfirmRemove(false)} className={BUTTON_QUIET} data-id="element-193" data-mp="2ophx6v15bsle">
              Cancel
            </button>
          </div>
        </div>}

      {open && <div className="mb-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-194" data-mp="1w245bbcb1n85">
          {editingRow && <div className="lg:col-span-7" data-id="element-195" data-mp="1r4lx5n643c17">
              {/* The deliverable being worked on leads the panel. */}
              <input value={draft.name} onChange={event => edit({
          name: event.target.value
        })} placeholder="e.g. Event Highlights Film" aria-label="Deliverable title" className="w-full border-0 border-b border-paper/25 bg-transparent pb-3 font-display text-3xl leading-none text-paper placeholder:text-paper/30 focus:border-paper focus:outline-none sm:text-4xl" data-id="element-196" data-mp="25xffdgcrq69k" />

              <div className="mt-9 grid grid-cols-1 gap-8 sm:grid-cols-2" data-id="element-197" data-mp="rer625p7x82c">
                <label className="block" data-id="element-198" data-mp="mmqz3taqdew9">
                  <span className="label block text-paper/40" data-id="element-199" data-mp="mlfvpul7695o">Type</span>
                  <select value={draft.type} onChange={event => edit({
              type: event.target.value as Deliverable['type']
            })} className={SELECT} data-id="element-200" data-mp="hnmsnaf6xv9m">
                    {DELIVERABLE_TYPES.map(option => <option key={option} value={option} className={OPTION} data-id="element-201" data-mp="3ppsk41j36q7">
                        {option}
                      </option>)}
                  </select>
                </label>

                <label className="block" data-id="element-202" data-mp="1y1cz2nts8vmf">
                  <span className="label block text-paper/40" data-id="element-203" data-mp="1y0bik3tzzgxb">Status</span>
                  {/* The client's wording sits inside the options, not on the page. */}
                  <select value={draft.status} onChange={event => edit({
              status: event.target.value as Deliverable['status']
            })} className={SELECT} data-id="element-204" data-mp="20mkn1phe4iwu">
                    {DELIVERABLE_STATUSES.map(option => <option key={option} value={option} className={OPTION} data-id="element-205" data-mp="1ho9g34dv4qgz">
                        {statusOptionLabel(option)}
                      </option>)}
                  </select>
                </label>

                {draft.type === 'Video' && <label className="block" data-id="element-206" data-mp="1roifss72cu14">
                    <span className="label block text-paper/40" data-id="element-207" data-mp="1wm1wrzv5bkwr">Video Format</span>
                    <select value={draft.videoFormat ?? '16:9 Landscape'} onChange={event => edit({
              videoFormat: event.target.value as Deliverable['videoFormat']
            })} className={SELECT} data-id="element-208" data-mp="331unxf9uvyt3">
                      {VIDEO_FORMATS.map(option => <option key={option} value={option} className={OPTION} data-id="element-209" data-mp="131pebzlm2d5o">
                          {option}
                        </option>)}
                    </select>
                  </label>}

                {versioned && <label className="block" data-id="element-210" data-mp="p86iglbtu2m7">
                    <span className="label block text-paper/40" data-id="element-211" data-mp="k93obvxdxgt0">Version</span>
                    <input value={draft.version} onChange={event => edit({
              version: event.target.value
            })} placeholder="e.g. V3" className={FIELD} data-id="element-212" data-mp="k70okk6fk9b3" />
                  </label>}
              </div>

              {draft.type === 'Transcript' && <TranscriptUpload value={draft.transcript ?? ''} onChange={text => edit({
          transcript: text
        })} data-id="element-213" data-mp="1huajagl05vm" />}

              {/* Only the links this type can actually use. */}
              {links.drive && <label className="mt-9 block" data-id="element-214" data-mp="3dmye3lgnslvm">
                  <span className="label block text-paper/40" data-id="element-215" data-mp="3ikq58d9hc3yr">{links.driveLabel}</span>
                  <input value={draft.driveUrl} onChange={event => edit({
            driveUrl: event.target.value
          })} placeholder="Paste Drive file or folder link" className={FIELD} data-id="element-216" data-mp="3iim0jt3rb0xf" />
                  {draft.driveUrl.trim() && <span className="label-sm mt-3 block text-paper/30" data-id="element-217" data-mp="55k2nfw3ppg6">
                      {toDrivePreviewUrl(draft.driveUrl) ? `Embeds as ${toDrivePreviewUrl(draft.driveUrl)}` : isDriveFolder(draft.driveUrl) ? 'Folder link · opens in Drive' : 'Not recognised as a Drive file'}
                    </span>}
                </label>}

              {links.audio && <label className="mt-9 block" data-id="element-218" data-mp="2wd4i4b2gwpac">
                  <span className="label block text-paper/40" data-id="element-219" data-mp="2wdw2squj12lu">Audio Review Link</span>
                  <input value={draft.audioUrl} onChange={event => edit({
            audioUrl: event.target.value
          })} placeholder="Paste Drive audio link" className={FIELD} data-id="element-220" data-mp="2rfcisvzhu0w4" />
                </label>}

              <label className="mt-9 flex items-center justify-between gap-6 border-b border-paper/25 pb-3" data-id="element-221" data-mp="3f2kjt1pw2nsc">
                <span className="label text-paper/40" data-id="element-222" data-mp="3a49acs3iafan">Shown in Client Portal</span>
                <button type="button" onClick={() => edit({
            visibleToClient: !draft.visibleToClient
          })} aria-pressed={draft.visibleToClient} className={['label border px-3 py-2 transition-colors duration-150 ease-out', draft.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-223" data-mp="23prstt4xa3dc">
                  {draft.visibleToClient ? 'On' : 'Off'}
                </button>
              </label>

              {/* Publishing is the significant act, so it carries the weight. */}
              <div className="mt-11 flex flex-wrap items-center gap-5" data-id="element-224" data-mp="11vclhevhbh24">
                <button type="button" onClick={publish} className={versioned ? BUTTON_SOLID : BUTTON_GHOST} data-id="element-225" data-mp="16xa7uhz6erza">
                  Publish {target} for Review
                </button>
                <button type="button" onClick={save} className={versioned ? BUTTON_GHOST : BUTTON_SOLID} data-id="element-226" data-mp="3dmowlu6e937v">
                  Save Changes
                </button>
              </div>
              <p className="label-sm mt-5 text-paper/30" data-id="element-227" data-mp="31lqlwaaogyus">Client sees these updates immediately</p>
            </div>}

          <div className={editingRow ? 'lg:col-span-5 lg:border-l lg:border-ruleDark lg:pl-12' : 'lg:col-span-7'} data-id="element-228" data-mp="3fi3s2n9rhdty">
            {/* Outstanding feedback leads, so nothing needing action is buried. */}
            {outstanding.length > 0 && <>
                <h4 className="label border-b border-ruleDark pb-4 text-paper/40" data-id="element-229" data-mp="3khgo9ymivx97">
                  Requested Changes
                </h4>
                <ul className="m-0 list-none p-0" data-id="element-230" data-mp="3tbbkui9kwfd8">
                  {outstanding.map(request => <li key={request.id} className="border-b border-ruleDark py-5" data-id="element-231" data-mp="c4j8tgzc5qkt">
                      <span className="label block text-paper" data-id="element-232" data-mp="74wrowgdzq6q">
                        {request.timecode ? `${request.timecode} · ` : ''}
                        {request.authorName}
                      </span>
                      <p className="mt-2 font-display text-lg leading-snug" data-id="element-233" data-mp="uqqpc3iafuor">{request.body}</p>
                      <div className="mt-3 flex items-center justify-between gap-4" data-id="element-234" data-mp="gssc51fpe4au">
                        <span className="label-sm text-paper/35" data-id="element-235" data-mp="lqkeyqhvzatt">
                          {request.version ? `${request.version} · ` : ''}
                          Sent {request.time || request.submitted}
                          {waitingLabel(request.at) ? ` · ${waitingLabel(request.at)}` : ''}
                        </span>
                        <button type="button" onClick={() => {
                  resolveChangeRequest(deliverable.id, request.id);
                  notify('Marked as completed');
                }} className="label-sm shrink-0 text-paper/60 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper" data-id="element-236" data-mp="3u86qtohdrk3k">
                          Mark completed
                        </button>
                      </div>
                    </li>)}
                </ul>
              </>}

            <div className={outstanding.length > 0 ? 'mt-12' : ''} data-id="element-237" data-mp="3qqaywai1mt7l">
              {deliverable.type === 'Transcript' ? <TranscriptRevisions deliverable={deliverable} data-id="element-238" data-mp="3lr7xd019v54d" /> : <VersionHistory deliverable={deliverable} data-id="element-239" data-mp="3gzprbxke6ecj" />}
            </div>

            {completed.length > 0 && <>
                <h4 className="label mt-12 border-b border-ruleDark pb-4 text-paper/40" data-id="element-240" data-mp="3cyw83hhw9npz">
                  Completed Changes
                </h4>
                <ul className="m-0 list-none p-0" data-id="element-241" data-mp="330zumj1higop">
                  {completed.map(request => <li key={request.id} className="border-b border-ruleDark py-5" data-id="element-242" data-mp="2y0vggzo1ty1n">
                      <span className="label block text-paper/60" data-id="element-243" data-mp="2nxoyaudifn3y">
                        {request.timecode ? `${request.timecode} · ` : ''}
                        {request.authorName}
                      </span>
                      <p className="mt-2 font-display text-lg leading-snug text-paper/60" data-id="element-244" data-mp="2xvcuzpuof4or">
                        {request.body}
                      </p>
                      <span className="label-sm mt-3 block text-paper/35" data-id="element-245" data-mp="32smq9m4qyaki">
                        {request.version ? `${request.version} · ` : ''}
                        Requested {request.time || request.submitted}
                        {request.completedAt ? ` · Completed ${request.completedAt}` : ' · Completed'}
                        {request.completedBy ? ` by ${request.completedBy}` : ''}
                      </span>
                    </li>)}
                </ul>
              </>}
          </div>
        </div>}
    </Reorder.Item>;
}